import React, { useState, useEffect, useRef, useMemo } from "react";
import { validateFormElements } from "../../../../utilities/formValidators";
import styled from "styled-components";
import useLanguage from "../../../../utilities/useLanguage";
import { getTranslation } from "../../translations";
import cx from "classnames";
import { InputCurrency, InputDropdown, OptionDropdown, TooltipTrigger } from '../../../../../';
import Picture from "../../../Picture";
import { formatValue } from "../../../../utilities/formatValue";
import { DROPDOWN_IDS } from "../../common/DropdownValues";
import { CONSTANTS } from "../../common/constants";
import RateBox from "../Rates/RateBox";
import { parseHTML } from "../../../../utilities/markdown";
import InfoIcon from "../../common/info-icon.svg";
import { calculateMonthlyPayment } from "./calculations";
import { IAprRates } from "../../models/IAprRates";
import { ICalculatedRate } from "../../models/IRate";
import { ILtvMap, IFormValues, IRateError } from "../../models";
import { getImagePath } from "../../../../utilities/path";
import { AutomationTestIdFeature } from "../../../../utilities/automation";

const Container = styled.div`
padding-top: 7rem !important;
padding-bottom: 7rem !important;
.title {
    font-size: 1.33333rem;
    font-weight: 350;
    line-height: 110%;
    letter-spacing: 0.05333rem;
    text-transform: uppercase;
}
.monthly-payment {
    font-size: 3.55556rem;
    line-height: 3.078rem;
    @media (min-width:576px) {
        font-size: 5.33333rem;
    }
    font-weight: 350;
}
.monthly-payment-label {
    font-size: 1.11111rem;
    font-weight: 700;
    line-height: 1.51561rem;
    text-transform: none;

    @media (min-width:576px) {
        font-size: 1.55556rem;
        line-height: 130.5%;
    }
}
.calc-box {
    z-index: 1;
    max-width: 69rem;
    border-radius: 0.125rem;
    box-shadow: 3px 3px 10px 4px rgba(0, 0, 0, 0.05);
}
.calc-wrapper {
    z-index: 1;
}
.top-left-image, .bottom-right-image {
    width: 18rem;
    height: 18rem;

    @media (min-width:576px){
        width: 28rem;
        height: 28rem;
    }

    @media (min-width:768px){
        width: 34rem;
        height: 34rem;
    }
    img {
        object-fit: contain;
    }
}
.top-left-image,
.bottom-right-image {
    transition: transform 0.5s;
}
.top-left-image {
    transform: translateY(15px);
    top: 0;
    left: -5rem;
    img {
        object-position: top left;
    }
}
.bottom-right-image {
    transform: translateY(15px);
    bottom: 0;
    right: -5rem;
    img {
        object-position: bottom right;
    }
}
.blur, .bg {
    position: absolute;
    height: 100%;
    width: 100%;
}
.bg {
    background: rgba(256,256,256,0.92);
}
.blur {
    -webkit-backdrop-filter: blur(10px) !important;
    backdrop-filter: blur(10px) !important;
    background-color: transparent !important;
    opacity: 1 !important;
}
.child-content{
    min-height: 10rem;
}
.overflow-hidden-x {
    overflow-x: hidden;
}
.error-message {
    font-size: 1.111rem;
}
.calculator-footer {
    color: #000;
    font-size: 0.77778rem;
    font-weight: 500;
    line-height: 1.11111rem;
    letter-spacing: 0.02333rem;
    p {
        margin: 0;
        display: inline-block;
        margin: auto;
    }
}
.detail-quote-link {
    font-size: 0.88889rem;
    font-weight: 500;
    line-height: 150%;
}
`;

export interface ICalcProps {
    onDetailsClick: () => void;
    values: IFormValues;
    onChange: (name: string, value: string) => void;
    onRateChange: (number, ICalculatedRate) => void;
    aprRates: IAprRates;
    headline: string;
    maxLTVRates: ILtvMap;
    cmsId?: string;
    className?: string;
    imageLeftSrc: string;
    imageRightSrc: string;
    imageLeftAlt: string;
    imageRightAlt: string;
    defaultSliderValue?: number;
    footerHtml: string;
}

export default (props: ICalcProps) => {
    const {
        headline,
        aprRates,
        values,
        onDetailsClick,
        onChange,
        onRateChange,
        maxLTVRates,
        cmsId,
        className,
        imageLeftSrc,
        imageRightSrc,
        imageLeftAlt,
        imageRightAlt,
        defaultSliderValue = 0,
        footerHtml,
     } = props;

    const { lang } = useLanguage();
    const wrapperRef = useRef(null); // used for detecting y position of wrapper when page scrolling
    const leftImageRef = useRef(null); // used for setting y position of image
    const rightImageRef = useRef(null); // used for setting y position of image
    const loanTerm = values.LoanTerm.value;
    const [sliderValue, setSliderValue] = useState(defaultSliderValue);
    const isPurchase = values.LoanPurpose.value === CONSTANTS.PURCHASE;
    const loanAmount = parseFloat(values.PurchasePrice.value) - parseFloat(values.DownPayment.value);

    const { selectedRate, rateError } = useMemo<{selectedRate: ICalculatedRate, rateError: IRateError}>(() => {
        if (!aprRates.loaded || !loanTerm) {
            return { selectedRate: null, rateError : null };
        }
        const selectedRate = aprRates.data.find(rate => rate.id === Number(values.LoanTerm.value));
        const downPaymentOrRefinance = parseInt(isPurchase ? values.DownPayment.value : values.RefinanceAmount.value);
        const purchasePriceOrHomeValue = parseInt(isPurchase ? values.PurchasePrice.value : values.CurrentHomeValue.value);
        const calculatedRate = calculateMonthlyPayment(selectedRate, downPaymentOrRefinance, purchasePriceOrHomeValue, isPurchase, sliderValue);
        const maxLTV = maxLTVRates ? (maxLTVRates[selectedRate.id] ?? maxLTVRates.default) : 0;
        const rateError = validateFormElements(
            values.LoanPurpose.value,
            parseInt(values.DownPayment.value),
            parseInt(values.RefinanceAmount.value),
            parseInt(values.PurchasePrice.value),
            parseInt(values.CurrentHomeValue.value),
            selectedRate.id,
            values.FirstTimeLimit.value,
            values.DefaultLoanLimit.value,
            maxLTV,
        );
        return {
            selectedRate: calculatedRate,
            rateError,
        };
    }, [
        values.LoanTerm.value,
        values.DownPayment.value,
        values.RefinanceAmount.value,
        values.PurchasePrice.value,
        values.CurrentHomeValue.value,
        values.FirstTimeLimit.value,
        values.DefaultLoanLimit.value,
        aprRates.loaded,
        aprRates.data,
        sliderValue,
        maxLTVRates,
    ]);

    useEffect(() => {
        onRateChange(loanAmount, selectedRate);
    }, [ loanAmount, selectedRate ]);

    function handleScroll() {
        // calculate the y position of the component in the viewport in percentage form.
        // (100% when TOP of component is at BOTTOM of viewport, 0% when BOTTOM of component is at TOP of viewport)
        const yPercentInViewPort = (Number(((wrapperRef.current.getBoundingClientRect().top+(wrapperRef.current.clientHeight))/(window.innerHeight+wrapperRef.current.clientHeight)).toFixed(3)));

        // start: top of image(s) at +15px (y), end: top of images at -15px (y) (30px movement per Cam)
        leftImageRef.current.style.transform = `translateY(${(20) - ((1-yPercentInViewPort)*40)}px)`;
        rightImageRef.current.style.transform = `translateY(${(20) - ((1-yPercentInViewPort)*40)}px)`;
    }

    useEffect(() => {
        const options = {
            threshold: 0,
        };

        const handleIntersection = (entries) => {
            // only one element being observed [0]
            if (entries[0]?.isIntersecting) {
                // only monitor scrolling when element is in viewport
                document.addEventListener('scroll', handleScroll);
            } else {
                document.removeEventListener('scroll', handleScroll);
            }
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current);
        }

        return () => {
            if (wrapperRef.current) {
                observer.unobserve(wrapperRef.current);
            }
        };
    }, []);

    function RateError() {
        if (!aprRates.loaded || !selectedRate) return null;
        return (<div className="error-message text-muted">
            {rateError.message}
        </div>);
    }

    function RateInfo() {
        if (!aprRates.loaded || !selectedRate) return null;
        return (<>
            <div className="d-flex">
                <div className="d-flex mt-4 mb-2 mb-lg-4">
                    <span className="monthly-payment">{formatValue({valueStr: String(selectedRate.monthlyPayment), precision: 2, formatType: '$'})}</span>
                    <TooltipTrigger className="ml-1" html={selectedRate.rate.product.disclosure.disclosureData} place="left"><img src={InfoIcon} /></TooltipTrigger>
                </div>
            </div>
            <h4 className="monthly-payment-label mt-sm-3 mb-2 mb-lg-4">{selectedRate.rateConfig.displayName}</h4>
            <button type="button" className="btn-link border-0 bg-transparent text-dark text-uppercase detail-quote-link" onClick={onDetailsClick}>{getTranslation(lang).detailedQuote}</button>
        </>);
    }

    return (
        <div ref={wrapperRef} className="overflow-hidden-x">
            <Container data-id={cmsId} className={cx(className, 'container my-6')}>
                <div ref={leftImageRef} className="position-absolute top-left-image">
                    <Picture loading="lazy" src={getImagePath(imageLeftSrc)} alt={imageLeftAlt} className="w-100 h-100 image" />
                </div>
                <div ref={rightImageRef} className="position-absolute bottom-right-image">
                    <Picture loading="lazy" src={getImagePath(imageRightSrc)} alt={imageRightAlt} className="w-100 h-100 image" />
                </div>
                <div className="d-flex flex-column calc-box m-auto flex-grow-1 position-relative">
                    <div className="bg"/>
                    <div className="blur"/>
                    <div className="p-2 p-md-5 calc-wrapper">
                        <div className="row">
                            <div className="col">
                                <h3 className="title mb-3 mt-4 mt-lg-0">{parseHTML(headline, 'MRQTv3 Headline')}</h3>
                            </div>
                        </div>
                        <div className="row">
                            <div className="form flex-grow-1 col-12 col-sm-7 col-md-6 col-lg-4 m-auto">
                                <InputCurrency id="purchasePriceOrCurrentHomeValue" inputType="currency" inputLabel={isPurchase ? getTranslation(lang).purchasePrice : getTranslation(lang).currentHomeValue} onChange={amount => onChange(isPurchase ? CONSTANTS.PURCHASE_PRICE_ID : CONSTANTS.CURRENT_VALUE_ID, amount || 0)} inputDefaultValue={isPurchase ? values.PurchasePrice.value : values.CurrentHomeValue.value} underline />
                                <InputCurrency id="downPaymentOrRefinanceAmount" inputType="currency" inputLabel={isPurchase ? getTranslation(lang).downPayment : getTranslation(lang).refinanceAmount} onChange={amount => onChange(isPurchase ? CONSTANTS.DOWN_PAYMENT_ID : CONSTANTS.REFINANCE_AMT_ID, amount || 0)} inputDefaultValue={isPurchase ? values.DownPayment.value : values.RefinanceAmount.value} underline />
                                <InputDropdown title={getTranslation(lang).loanType} onChange={(loanTerm) => onChange(DROPDOWN_IDS.LOAN_TERM, loanTerm)} autoSelect feature={AutomationTestIdFeature.MRQT_CALCULATOR}>
                                    {aprRates.data.map(r => {
                                        return <OptionDropdown key={r.id} eventKey={r.id} html={r.rateConfig.displayName}/>;
                                    })}
                                </InputDropdown>
                                {selectedRate ? <RateBox selectedRate={selectedRate} handleInputSliderChange={setSliderValue} /> : null}
                            </div>
                            <div className="d-flex flex-column flex-grow-1 text-center align-items-center justify-content-center payment-display col-12 col-lg-8">
                                <div className="p-4">
                                    {rateError?.isError ? <RateError /> : <RateInfo />}
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12 d-flex text-center calculator-footer">
                                {parseHTML(footerHtml, 'MRQTv3 Footer')}
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};
